import { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
    updateOperatorCompanyService,
    uploadOperatorCompanyLogoService,
    uploadOperatorCompanyPhotoService,
} from "../../../../Services/CRMAdminServices/CRMOperator";
import { formatAbn, formatAcn, ABN_INPUT_MAX_LEN, ACN_INPUT_MAX_LEN } from "../../../../Utils/companyNumber";
import { checkMemberImage, MEMBER_IMAGE_ACCEPT } from "./memberImage";

// Richard, 5 Oct 2026: "CRM should control all and Platform just display."
// Every detail of a member that the member can set on the Platform, Fintrack can set
// here. Both write the same record, so there is one copy.

// Same swatches as the Create member form and the member's own Company Settings.
const COLOR_OPTIONS = ["#1B4FE0", "#0E2570", "#16803b", "#F5B301", "#b0392e", "#6b21a8"];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Details = {
    businessName: string;
    shortName: string;
    acn: string;
    abn: string;
    contactName: string;
    contactMobile: string;
    contactEmail: string;
    bookingLink: string;
    brandColorPrimary: string;
    brandColorSecondary: string;
};

const fromCompany = (company: any): Details => ({
    businessName: company?.businessName || "",
    shortName: company?.shortName || "",
    acn: company?.acn ? formatAcn(company.acn) : "",
    abn: company?.abn ? formatAbn(company.abn) : "",
    contactName: company?.contactName || "",
    contactMobile: company?.contactMobile || "",
    contactEmail: company?.contactEmail || "",
    bookingLink: company?.bookingLink || "",
    brandColorPrimary: company?.brandColorPrimary || "",
    brandColorSecondary: company?.brandColorSecondary || "",
});

type Props = {
    company: any;
    isOpen: boolean;
    toggle: () => void;
    // Called after anything is saved, so the page behind re-reads the member.
    onSaved: () => void;
};

const EditMemberDetails = ({ company, isOpen, toggle, onSaved }: Props) => {
    const [form, setForm] = useState<Details>(fromCompany(company));
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [uploading, setUploading] = useState<"" | "logo" | "photo">("");
    const [logoUrl, setLogoUrl] = useState<string>(company?.logoUrl || "");
    const [photoUrl, setPhotoUrl] = useState<string>(company?.contactPhotoUrl || "");

    // Start from what is stored every time the window is opened, so a change that
    // was typed and then cancelled is not still sitting there next time.
    useEffect(() => {
        if (isOpen) {
            setForm(fromCompany(company));
            setErrors({});
            setLogoUrl(company?.logoUrl || "");
            setPhotoUrl(company?.contactPhotoUrl || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const set = (field: keyof Details, value: string) => {
        let v = value;
        if (field === "acn") v = formatAcn(value);
        if (field === "abn") v = formatAbn(value);
        setForm((prev) => ({ ...prev, [field]: v }));
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    const validate = () => {
        const next: Record<string, string> = {};
        if (!form.businessName.trim()) next.businessName = "Business name is required.";
        if (form.contactEmail.trim() && !EMAIL_REGEX.test(form.contactEmail.trim())) {
            next.contactEmail = "Enter a valid email address.";
        }
        if (form.bookingLink.trim()) {
            try {
                new URL(form.bookingLink.trim());
            } catch {
                next.bookingLink = "Enter a full URL, e.g. https://calendly.com/you";
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const blank = (v: string) => (v.trim() ? v.trim() : null);

    const save = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            await updateOperatorCompanyService(company.companyId, {
                businessName: form.businessName.trim(),
                shortName: blank(form.shortName),
                acn: blank(form.acn),
                abn: blank(form.abn),
                contactName: blank(form.contactName),
                contactMobile: blank(form.contactMobile),
                contactEmail: blank(form.contactEmail),
                bookingLink: blank(form.bookingLink),
                brandColorPrimary: blank(form.brandColorPrimary),
                brandColorSecondary: blank(form.brandColorSecondary),
            });
            toast.success("Details saved.");
            onSaved();
            toggle();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Could not save the details.");
        } finally {
            setIsSaving(false);
        }
    };

    // An image saves the moment it is chosen. There is no second button to press,
    // because a picture that looks uploaded and is not is worse than no picture.
    const upload = async (which: "logo" | "photo", file: File | null) => {
        if (!file) return;
        const what = which === "logo" ? "logo" : "contact photo";
        const problem = checkMemberImage(file, what);
        if (problem) {
            toast.error(problem);
            return;
        }
        setUploading(which);
        try {
            if (which === "logo") {
                const res: any = await uploadOperatorCompanyLogoService(company.companyId, file);
                setLogoUrl(res?.data?.logoUrl || res?.data?.LogoUrl || "");
                toast.success("Logo saved.");
            } else {
                const res: any = await uploadOperatorCompanyPhotoService(company.companyId, file);
                setPhotoUrl(res?.data?.contactPhotoUrl || res?.data?.ContactPhotoUrl || "");
                toast.success("Photo saved.");
            }
            onSaved();
        } catch (err: any) {
            toast.error(err?.response?.data?.message || `Could not upload the ${what}.`);
        } finally {
            setUploading("");
        }
    };

    const swatches = (field: "brandColorPrimary" | "brandColorSecondary") => (
        <div className="d-flex" style={{ gap: 8 }}>
            {COLOR_OPTIONS.map((c) => (
                <button
                    key={c}
                    type="button"
                    onClick={() => set(field, c)}
                    style={{
                        width: 30,
                        height: 30,
                        borderRadius: 8,
                        background: c,
                        cursor: "pointer",
                        border: form[field].toLowerCase() === c.toLowerCase() ? "3px solid #0E2570" : "1px solid #dee2e6",
                    }}
                    aria-label={c}
                />
            ))}
        </div>
    );

    const preview = (url: string, alt: string) =>
        url ? (
            <img
                src={url}
                alt={alt}
                style={{ maxHeight: 70, maxWidth: 160, objectFit: "contain", display: "block", marginBottom: 8 }}
            />
        ) : (
            <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                None yet.
            </div>
        );

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>Edit details</ModalHeader>
            <ModalBody>
                <div className="row mb-3">
                    <div className="col-md-6">
                        <label>Business name *</label>
                        <input
                            className={`form-control ${errors.businessName ? "is-invalid" : ""}`}
                            value={form.businessName}
                            maxLength={100}
                            onChange={(e) => set("businessName", e.target.value)}
                        />
                        {errors.businessName && <div className="invalid-feedback d-block">{errors.businessName}</div>}
                    </div>
                    <div className="col-md-6">
                        <label>Short name</label>
                        <input
                            className="form-control"
                            value={form.shortName}
                            maxLength={40}
                            onChange={(e) => set("shortName", e.target.value)}
                        />
                        <small className="text-muted">What their clients see in a text. Blank uses the business name.</small>
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <label>ACN</label>
                        <input
                            className="form-control"
                            value={form.acn}
                            maxLength={ACN_INPUT_MAX_LEN}
                            onChange={(e) => set("acn", e.target.value)}
                        />
                    </div>
                    <div className="col-md-6">
                        <label>ABN</label>
                        <input
                            className="form-control"
                            value={form.abn}
                            maxLength={ABN_INPUT_MAX_LEN}
                            onChange={(e) => set("abn", e.target.value)}
                        />
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-4">
                        <label>Contact name</label>
                        <input
                            className="form-control"
                            value={form.contactName}
                            maxLength={60}
                            onChange={(e) => set("contactName", e.target.value)}
                        />
                    </div>
                    <div className="col-md-4">
                        <label>Contact mobile</label>
                        <input
                            className="form-control"
                            value={form.contactMobile}
                            maxLength={12}
                            onChange={(e) => set("contactMobile", e.target.value)}
                        />
                    </div>
                    <div className="col-md-4">
                        <label>Contact email</label>
                        <input
                            type="email"
                            className={`form-control ${errors.contactEmail ? "is-invalid" : ""}`}
                            value={form.contactEmail}
                            maxLength={40}
                            onChange={(e) => set("contactEmail", e.target.value)}
                        />
                        {errors.contactEmail && <div className="invalid-feedback d-block">{errors.contactEmail}</div>}
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-12">
                        <label>Booking link</label>
                        <input
                            className={`form-control ${errors.bookingLink ? "is-invalid" : ""}`}
                            value={form.bookingLink}
                            maxLength={200}
                            placeholder="https://cal.com/..."
                            onChange={(e) => set("bookingLink", e.target.value)}
                        />
                        {errors.bookingLink && <div className="invalid-feedback d-block">{errors.bookingLink}</div>}
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col-md-6">
                        <label>Brand colour — Primary</label>
                        {swatches("brandColorPrimary")}
                    </div>
                    <div className="col-md-6">
                        <label>Brand colour — Secondary</label>
                        {swatches("brandColorSecondary")}
                    </div>
                </div>

                <hr />

                <div className="row">
                    <div className="col-md-6">
                        <label>Logo</label>
                        {preview(logoUrl, "Logo")}
                        <input
                            type="file"
                            accept={MEMBER_IMAGE_ACCEPT}
                            className="form-control"
                            disabled={uploading !== ""}
                            onChange={(e) => {
                                upload("logo", e.target.files?.[0] || null);
                                e.target.value = "";
                            }}
                        />
                        <small className="text-muted">
                            {uploading === "logo" ? "Uploading..." : "PNG or JPG, 2MB or smaller. Saves as soon as it is chosen."}
                        </small>
                    </div>
                    <div className="col-md-6">
                        <label>Contact photo</label>
                        {preview(photoUrl, "Contact photo")}
                        <input
                            type="file"
                            accept={MEMBER_IMAGE_ACCEPT}
                            className="form-control"
                            disabled={uploading !== ""}
                            onChange={(e) => {
                                upload("photo", e.target.files?.[0] || null);
                                e.target.value = "";
                            }}
                        />
                        <small className="text-muted">
                            {uploading === "photo"
                                ? "Uploading..."
                                : "Shown to their clients on their Gameplan. Saves as soon as it is chosen."}
                        </small>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={toggle} disabled={isSaving}>
                    Cancel
                </Button>
                <Button color="primary" onClick={save} disabled={isSaving || uploading !== ""}>
                    {isSaving ? (
                        <>
                            <Spinner size="sm" /> Saving...
                        </>
                    ) : (
                        "Save details"
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default EditMemberDetails;
